import { createAsyncThunk } from "@reduxjs/toolkit";

import { HttpError, fetchJson } from "../../../utils/ajax";
import { IBasket, ICustomerDetails } from "../models";

interface ILookupBasketResult {
    basket: IBasket;
    owner: ICustomerDetails;
}

/**
 * Looks up a basket by its reference number and fetches the owner's details.
 *
 * This is typically used in the Basket Search panel to find specific baskets
 * by their encoded basket ID or reference number. After finding the basket,
 * it also fetches the full customer details of the basket owner.
 *
 * @param reference_number - The basket reference number or encoded basket ID to search for
 * @returns Promise resolving to basket and owner details
 * @throws Error if basket is not found or owner cannot be loaded
 *
 * @example
 * dispatch(lookupBasket("ABC123"))
 */
export const lookupBasket = createAsyncThunk<
    ILookupBasketResult,
    string,
    { rejectValue: string }
>("csr/lookupBasket", async (reference_number, { rejectWithValue }) => {
    try {
        const params = new URLSearchParams({ q: reference_number });
        const basket = await fetchJson<IBasket | null>(
            `/api/csr/baskets/lookup/?${params}`,
        );

        if (!basket) {
            return rejectWithValue("No basket found");
        }

        const owner = await fetchJson<ICustomerDetails>(basket.owner);
        return {
            basket: basket,
            owner: owner,
        };
    } catch (err) {
        if (err instanceof HttpError) {
            const body = err.body as { q?: unknown; detail?: unknown };
            const message =
                typeof body?.q === "string"
                    ? body.q
                    : typeof body?.detail === "string"
                      ? body.detail
                      : err.message;
            return rejectWithValue(message);
        }
        const message =
            err instanceof Error
                ? err.message
                : "An error occurred while looking up the basket";
        return rejectWithValue(message);
    }
});
